import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/AuthContext";
import { CountryProvider } from "@/lib/CountryContext";
import { ProtectedRoute, RoleRedirect } from "@/lib/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";

import PublicLayout from "@/components/PublicLayout";
import DashboardLayout from "@/components/DashboardLayout";

import Landing from "@/pages/public/Landing";
import Browse from "@/pages/public/Browse";
import GroupDetail from "@/pages/public/GroupDetail";
import Pricing from "@/pages/public/Pricing";
import FAQ from "@/pages/public/FAQ";
import BecomeVendor from "@/pages/public/BecomeVendor";
import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import SubPayPage from "@/pages/public/SubPayPage";
import Join from "@/pages/public/Join";
import AffiliateApply from "@/pages/public/AffiliateApply";
import AffiliateApplySuccess from "@/pages/public/AffiliateApplySuccess";
import { PaymentSuccess, PaymentCancel } from "@/pages/public/PaymentResult";

import MemberOverview from "@/pages/member/Overview";
import MemberDeals from "@/pages/member/Deals";
import DealDetail from "@/pages/member/DealDetail";
import Subscriptions from "@/pages/member/Subscriptions";
import Payments from "@/pages/member/Payments";
import Profile from "@/pages/member/Profile";
import SecondaryUsers from "@/pages/member/SecondaryUsers";
import Employees from "@/pages/member/Employees";
import Calculator from "@/pages/member/Calculator";
import HelperInsurance from "@/pages/member/HK_Helper_Insurance";
import HKPropertyInsurance from "@/pages/member/HK-PropertyInsurance";
import HKTravelInsurance from "@/pages/member/HK-TravelInsurance";

import AffiliateDashboard from "@/pages/affiliate/Dashboard";
import AffiliatePayments from "@/pages/affiliate/Payments";
import AffiliatePayoutDetails from "@/pages/affiliate/PayoutDetails";

import BrokerDashboard from "@/pages/broker/Dashboard";
import BrokerProfiles from "@/pages/broker/Profiles";
import BrokerProducts from "@/pages/broker/Products";
import BrokerProductDetail from "@/pages/broker/ProductDetail";
import BrokerPurchases from "@/pages/broker/Purchases";
import BrokerPayoutDetails from "@/pages/broker/PayoutDetails";
import BrokerApplications from "@/pages/broker/Applications";
import BrokerApplicationDetail from "@/pages/broker/ApplicationDetail";

import { buildAdminRoutes } from "@/admin/adminRoutes";
import NotFound from "@/pages/public/NotFound";
import SEOHead from "@/components/SEOHead";

import "@/App.css";

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <CountryProvider>
                <SEOHead />
                <Routes>
                    {/* Public */}
                    <Route element={<PublicLayout />}>
                        <Route path="/" element={<Landing />} />
                        <Route path="/browse" element={<Browse />} />
                        <Route path="/browse/:groupId" element={<GroupDetail />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/faq" element={<FAQ />} />
                        <Route path="/become-a-vendor" element={<BecomeVendor />} />
                    </Route>

                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/join" element={<Join />} />
                    <Route path="/affiliates/apply" element={<AffiliateApply />} />
                    <Route path="/affiliates/apply/success" element={<AffiliateApplySuccess />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/pay/sub/:token" element={<SubPayPage />} />
                    <Route path="/payment/success" element={<PaymentSuccess />} />
                    <Route path="/payment/cancel" element={<PaymentCancel />} />

                    {/* Member */}
                    <Route element={<ProtectedRoute roles={["member"]} />}>
                        <Route element={<DashboardLayout />}>
                            <Route path="/member" element={<MemberOverview />} />
                            <Route path="/member/deals" element={<MemberDeals />} />
                            <Route path="/member/deals/:dealId" element={<DealDetail />} />
                            <Route path="/member/subscriptions" element={<Subscriptions />} />
                            <Route path="/member/payments" element={<Payments />} />
                            <Route path="/member/family" element={<SecondaryUsers />} />
                            <Route path="/member/employees" element={<Employees />} />
                            <Route path="/calculator" element={<Calculator />} />
                                <Route path="/member/helper-insurance" element={<HelperInsurance />} />
                                <Route path="/member/hk-property-insurance" element={<HKPropertyInsurance />} />
                                <Route path="/member/hk-travel-insurance" element={<HKTravelInsurance />} />
                            <Route path="/member/profile" element={<Profile />} />
                        </Route>
                    </Route>

                    {/* Broker */}
                    <Route element={<ProtectedRoute roles={["broker"]} />}>
                        <Route element={<DashboardLayout />}>
                            <Route path="/broker" element={<BrokerDashboard />} />
                            <Route path="/broker/profiles" element={<BrokerProfiles />} />
                            <Route path="/broker/products" element={<BrokerProducts />} />
                            <Route path="/broker/products/:productId" element={<BrokerProductDetail />} />
                            <Route path="/broker/purchases" element={<BrokerPurchases />} />
                            <Route path="/broker/payout-details" element={<BrokerPayoutDetails />} />
                            <Route path="/broker/applications" element={<BrokerApplications />} />
                            <Route path="/broker/applications/:appId" element={<BrokerApplicationDetail />} />
                        </Route>
                    </Route>

                    {/* Affiliate */}
                    <Route element={<ProtectedRoute roles={["affiliate"]} />}>
                        <Route element={<DashboardLayout />}>
                            <Route path="/affiliate" element={<AffiliateDashboard />} />
                            <Route path="/affiliate/payments" element={<AffiliatePayments />} />
                            <Route path="/affiliate/payout-details" element={<AffiliatePayoutDetails />} />
                            <Route path="/affiliate/profile" element={<Profile />} />
                        </Route>
                    </Route>

                    {/* Admin (feature slice — see @/admin/adminRoutes) */}
                    {buildAdminRoutes()}

                    <Route path="/404" element={<NotFound />} />
                    <Route path="*" element={<RoleRedirect />} />
                </Routes>
                </CountryProvider>
            </BrowserRouter>
            <Toaster
                position="top-right"
                toastOptions={{
                    style: {
                        background: "#001c50",
                        color: "#FAFAF7",
                        border: "1px solid #D4AF37",
                    },
                }}
            />
        </AuthProvider>
    );
}
